import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, Sun, Moon, Monitor, Palette } from 'lucide-react'
import { useUIStore } from '@/store/uiStore'
import { cn } from '@/utils'

export function AppearanceModal() {
  const {
    appearanceModalOpen,
    setAppearanceModalOpen,
    theme,
    setTheme,
    colorScheme,
    setColorScheme,
    chatBackground,
    setChatBackground,
  } = useUIStore()

  const themes = [
    { id: 'light' as const, label: 'Light', icon: Sun },
    { id: 'dark' as const, label: 'Dark', icon: Moon },
    { id: 'system' as const, label: 'System', icon: Monitor },
  ]

  const schemes = [
    { id: 'green' as const, label: 'Safari', shortLabel: '🌿', color: '#4CD137' },
    { id: 'blue' as const, label: 'Ocean', shortLabel: '🌊', color: '#3B82F6' },
    { id: 'purple' as const, label: 'Royal', shortLabel: '👑', color: '#8B5CF6' },
    { id: 'orange' as const, label: 'Sunset', shortLabel: '🌅', color: '#F59E0B' },
    { id: 'pink' as const, label: 'Rose', shortLabel: '🌸', color: '#EC4899' },
    { id: 'classic' as const, label: 'Classic', shortLabel: '💬', color: '#25D366' },
  ]

  const backgrounds = [
    { id: 'pattern' as const, label: 'Pattern', preview: 'bg-pattern-preview' },
    { id: 'gradient' as const, label: 'Gradient', preview: 'bg-gradient-preview' },
    { id: 'solid' as const, label: 'Solid', preview: 'bg-solid-preview' },
    { id: 'waves' as const, label: 'Waves', preview: 'bg-waves-preview' },
    { id: 'dots' as const, label: 'Dots', preview: 'bg-dots-preview' },
  ]

  if (!appearanceModalOpen) return null

  return (
    <AnimatePresence>
      {appearanceModalOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setAppearanceModalOpen(false)}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto pointer-events-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Palette className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Appearance</h2>
                    <p className="text-sm text-white/50">Customize your experience</p>
                  </div>
                </div>
                <button
                  onClick={() => setAppearanceModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              <div className="p-6 space-y-8">
                {/* Theme Selection */}
                <div>
                  <h3 className="text-sm font-medium text-white/70 mb-4">Theme</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {themes.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={cn(
                          'p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2',
                          theme === t.id
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-white/10 hover:border-white/20 bg-white/5'
                        )}
                      >
                        <t.icon className={cn('w-6 h-6', theme === t.id ? 'text-emerald-400' : 'text-white/60')} />
                        <span className={cn('text-sm', theme === t.id ? 'text-white' : 'text-white/60')}>
                          {t.label}
                        </span>
                        {theme === t.id && (
                          <Check className="w-4 h-4 text-emerald-400 absolute top-2 right-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Scheme */}
                <div>
                  <h3 className="text-sm font-medium text-white/70 mb-4">Color Scheme</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {schemes.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setColorScheme(s.id)}
                        className={cn(
                          'flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all',
                          colorScheme === s.id
                            ? 'border-white bg-white/10'
                            : 'border-white/10 hover:border-white/30 bg-white/5'
                        )}
                      >
                        <div 
                          className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center text-lg',
                            colorScheme === s.id && 'ring-2 ring-white ring-offset-2 ring-offset-transparent'
                          )}
                          style={{ backgroundColor: s.color }}
                        >
                          {colorScheme === s.id ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <span>{s.shortLabel}</span>
                          )}
                        </div>
                        <span className={cn(
                          'text-xs font-medium',
                          colorScheme === s.id ? 'text-white' : 'text-white/60'
                        )}>
                          {s.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Chat Background */}
                <div>
                  <h3 className="text-sm font-medium text-white/70 mb-4">Chat Background</h3>
                  <div className="grid grid-cols-5 gap-3">
                    {backgrounds.map((bg) => (
                      <button
                        key={bg.id}
                        onClick={() => setChatBackground(bg.id)}
                        className={cn(
                          'aspect-square rounded-xl border-2 transition-all relative overflow-hidden',
                          chatBackground === bg.id
                            ? 'border-white ring-2 ring-white/30'
                            : 'border-white/10 hover:border-white/30'
                        )}
                      >
                        {/* Background Preview */}
                        <div
                          className={cn(
                            'absolute inset-0',
                            bg.id === 'pattern' && 'bg-[#134422]',
                            bg.id === 'gradient' && 'bg-gradient-to-br from-emerald-900/50 to-emerald-950',
                            bg.id === 'solid' && 'bg-gray-900',
                            bg.id === 'waves' && 'bg-[#1a3d1a]',
                            bg.id === 'dots' && 'bg-[#0f2d0f]'
                          )}
                        >
                          {/* Pattern overlay for pattern type */}
                          {bg.id === 'pattern' && (
                            <div className="absolute inset-0 opacity-30" style={{
                              backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234CD137' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`,
                              backgroundSize: '10px 10px'
                            }} />
                          )}
                          {bg.id === 'waves' && (
                            <div className="absolute inset-0 opacity-20" style={{
                              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 Q25 0 50 10 T100 10' fill='none' stroke='%234CD137' stroke-width='2'/%3E%3C/svg%3E")`,
                              backgroundSize: '50px 10px'
                            }} />
                          )}
                          {bg.id === 'dots' && (
                            <div className="absolute inset-0 opacity-30" style={{
                              backgroundImage: `radial-gradient(circle, #4CD137 1px, transparent 1px)`,
                              backgroundSize: '8px 8px'
                            }} />
                          )}
                        </div>
                        {chatBackground === bg.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    {backgrounds.find(b => b.id === chatBackground)?.label}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-white/10">
                <button
                  onClick={() => setAppearanceModalOpen(false)}
                  className="btn-primary w-full"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
